import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { DateCustomFieldFilter } from '~/components/Filters/CustomFieldFilter/DateCustomFieldFilter';
import { setDateCustomFieldFilterAction } from '~/store/filters/actions';
import { dateCustomFieldFilterSelector } from '~/store/filters/selectors';
import { type DateCustomFieldFilter as DateCustomFieldFilterState } from '~/store/filters/types';
import {
  calendarDateFromCustomFieldInstant,
  customFieldDateInstantFromCalendarDay,
  toAssetCustomFieldDateFilter,
} from '~/utils/filters/dateCustomFieldFilter';
import { useAirSelector } from '~/utils/ReduxUtils';

export type DateCustomFieldFilterControlsProps = {
  customFieldId: string;
  fieldName: string;
  onCommitted?: () => void;
};

const rangeFilter = ({
  start,
  end,
}: {
  start: string | undefined;
  end: string | undefined;
}): DateCustomFieldFilterState => ({
  operator: 'range',
  ...(start ? { start } : {}),
  ...(end ? { end } : {}),
});

export const DateCustomFieldFilterControls = memo(
  ({ customFieldId, fieldName, onCommitted }: DateCustomFieldFilterControlsProps) => {
    const dispatch = useDispatch();
    const dateFilter = useAirSelector((state) => dateCustomFieldFilterSelector(state, customFieldId));
    const operator = dateFilter?.operator ?? 'range';
    const rangeStart = dateFilter?.operator === 'range' ? dateFilter.start : undefined;
    const rangeEnd = dateFilter?.operator === 'range' ? dateFilter.end : undefined;

    const handleCommit = useCallback(
      (next: DateCustomFieldFilterState) => {
        const previousActive = dateFilter ? toAssetCustomFieldDateFilter(dateFilter) !== null : false;

        dispatch(setDateCustomFieldFilterAction({ customFieldId, date: next }));
        if (previousActive || toAssetCustomFieldDateFilter(next) !== null) {
          onCommitted?.();
        }
      },
      [customFieldId, dateFilter, dispatch, onCommitted],
    );

    const handleOperatorChange = useCallback(
      (nextOperator: DateCustomFieldFilterState['operator']) => {
        if (nextOperator === 'notSet') {
          handleCommit({ operator: 'notSet' });
          return;
        }

        handleCommit(rangeFilter({ start: rangeStart, end: rangeEnd }));
      },
      [handleCommit, rangeEnd, rangeStart],
    );

    const handleStartDateChange = useCallback(
      (value: Date | [Date, Date] | null) => {
        if (Array.isArray(value)) {
          return;
        }

        const start = value ? customFieldDateInstantFromCalendarDay({ day: value, edge: 'start' }) : undefined;

        handleCommit(rangeFilter({ start, end: rangeEnd }));
      },
      [handleCommit, rangeEnd],
    );

    const handleEndDateChange = useCallback(
      (value: Date | [Date, Date] | null) => {
        if (Array.isArray(value)) {
          return;
        }

        const end = value ? customFieldDateInstantFromCalendarDay({ day: value, edge: 'end' }) : undefined;

        handleCommit(rangeFilter({ start: rangeStart, end }));
      },
      [handleCommit, rangeStart],
    );

    return (
      <DateCustomFieldFilter
        endDate={calendarDateFromCustomFieldInstant(rangeEnd)}
        fieldName={fieldName}
        onEndDateChange={handleEndDateChange}
        onOperatorChange={handleOperatorChange}
        onStartDateChange={handleStartDateChange}
        operator={operator}
        startDate={calendarDateFromCustomFieldInstant(rangeStart)}
      />
    );
  },
);

DateCustomFieldFilterControls.displayName = 'DateCustomFieldFilterControls';
